/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef } from "react";

import styled from "styled-components";

import { XButton } from "../Buttons";
import { HOTKEYS } from "src/components/Hotkeys/hotkeys";
import { useHotkey } from "src/lib/hooks/useHotkey";

interface Props {
  children: React.ReactNode | React.ReactNode[];
  close: (e?: React.MouseEvent) => void; // function to close the modal
  title: string;
  styles?: React.CSSProperties;
}
export const Modal = ({ children, close, title, styles }: Props) => {
  const backgroundRef = useRef(null);
  useHotkey(HOTKEYS.ESCAPE, () => close(), [], { stopPropagation: true, preventDefault: true });

  const handleClickedOverlay = (e: React.MouseEvent) => {
    if (e.target === backgroundRef.current) {
      e.stopPropagation();
      close();
    }
  };

  return (
    <ModalOverlay ref={backgroundRef} onClick={handleClickedOverlay}>
      <StyledModal style={styles}>
        <StyledHeader>
          <CloseWrapper>
            <XButton onClick={close} />
          </CloseWrapper>
          <ModalTitle>{title}</ModalTitle>
        </StyledHeader>
        {children}
      </StyledModal>
    </ModalOverlay>
  );
};

// Background Overlay
export const ModalOverlay = styled.div`
  line-height: 1.15;
  font-family: "IBM Plex Mono", monospace;
  background-color: ${({ theme }) => theme.modal.background};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-size: 14px;
  overflow: hidden;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 999;
  padding-left: 40px;
  padding-right: 40px;
  padding-bottom: 32px;
  padding-top: 40px;
`;

const StyledModal = styled.div`
  position: absolute;
  top: 65px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height: 80%;
  max-width: 80%;
  min-width: 500px;
  padding: 40px;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid #494949;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;
const ModalTitle = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-weight: 500;
  font-size: 20px;
  line-height: 20px;
  color: ${({ theme }) => theme.text.default};
`;

const CloseWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;
`;
